import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ITutorialStore} from "data/stores/tutorial/tutorial.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {IReactionDisposer, makeAutoObservable, observable, reaction} from "mobx";
import {AdobeDTM} from "data/utils/AdobeDTM";

export interface IModalTutorialController extends ViewController {
	close: () => void;
	onPlay: () => void;

	get isOpen(): boolean;

	get isSliderStep(): boolean;

	get isFirstStep(): boolean;

	get isLastStep(): boolean;
}

@injectable()
export class ModalTutorialController implements IModalTutorialController {
	@observable private _subscriptions$: IReactionDisposer[] = [];
	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.TutorialStore) private _tutorialStore: ITutorialStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get isFirstStep(): boolean {
		return this._tutorialStore.isFirstStep;
	}

	get isLastStep(): boolean {
		return this._tutorialStore.isLastStep;
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.TUTORIAL;
	}

	get isSliderStep() {
		return this._tutorialStore.isSliderStep;
	}

	public onPlay = () => {
		this._tutorialStore.onPlay();
	};

	dispose(): void {
		this._subscriptions$.forEach((dispose) => dispose());
		this._tutorialStore.clear();
	}

	private get isReturningUser(): boolean {
		return !this._userStore.ssoDataForRegistration;
	}

	init(param: void): void {
		const subscription = reaction(
			() => this.isReturningUser,
			() => this.checkModalState(),
			{fireImmediately: true}
		);
		this._subscriptions$.push(subscription);
		this._subscriptions$.push(
			reaction(
				() => this.isOpen,
				(isOpen) => {
					if (isOpen) {
						AdobeDTM.trackAction({
							"action.name": "Spinball: Welcome Modal Impression",
						});
					}
				}
			)
		);
	}

	onChange(param: void): void {
		return;
	}

	public close = () => {
		this._tutorialStore.setTutorialViewed();
		this._modalsStore.hideModal();
		this._tutorialStore.clear();
	};

	private checkModalState() {
		if (!this.isReturningUser) {
			return;
		}

		const isViewed = this._tutorialStore.getIsTutorialViewed();
		if (!isViewed) {
			this._modalsStore.showModal(ModalType.TUTORIAL);
		}
	}
}
